import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  alpha,
  FormControl,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ShippingStatusChip from '../../components/ShippingStatusChip';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { QRCode } from 'react-qr-code';

import CarSummary from './CarSummary';
import Checkin from './Checkin';
import Inspection from './Inspection';
import Service from './Service';
import Marketing from './Marketing';
import Facebook from './Facebook';
import Writeup from './Writeup';
import SalesReview from './SalesReview';
import Consignor from './Consignor';
import Leads from './Leads';
import Sales from './Sales';
import Finance from './Finance';
import ShippingIn from './ShippingIn';
import Shipping from './Shipping';
import Transport from './Transport';
import DMV from './DMV';
import Files from './Files';
import Notes from './Notes';
import Chat from './Chat';
import SoldLock, { SoldLockContext } from './SoldLock';

import firebase from '../../utilities/firebase';
import history from '../../utilities/history';
import { StateManager } from '../../utilities/stateManager';
import usePermissions from '../../utilities/usePermissions';
import ExpenseModal from '../../components/ExpenseModal';
import constants from '../../utilities/constants';
import { useAutoZ, ModalStackProvider } from '../../utilities/ModalStackContext';
import { useTallDialogSizing } from '../../utilities/dialogSizing';
import moment from 'moment';
import SizedImg from '../SizedImg';

const getDocData = doc => ({ id: doc.id, ...doc.data() });

const VISIBLE_TABS = [
  'summary', 'sales-review', 'consignment', 'checkin', 'service', 'marketing',
  'writeup', 'chat', 'leads', 'sales', 'finance', 'transport', 'DMV', 'files', 'notes',
];

// Deal money and paperwork. These were visible to anyone who could open a car, which
// meant mechanics and porters could read invoices, payoffs and title status. Gated on
// 'car-financials', which the sales/manager/backoffice presets carry (admins bypass).
const FINANCIAL_TABS = ['sales', 'finance', 'DMV'];

// Hidden tabs (kept for re-enabling later): facebook, shipping-in, shipping-out
//
// The 'title' tab is gone. Its fields now open from the DMV tab's Packet
// Readiness card as the Title App Data dialog, so all title work sits in one
// place. The titles/{stock} data is still loaded below — the dialog reads it.

const formatTitle = raw => {
  const spaced = raw.split('-').join(' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const STATUS_COLOR = {
  active: 'success',
  success: 'success',
  sold: 'primary',
  terminated: 'error',
  intake: 'warning',
  marketing: 'warning',
  holding: 'default',
  'admin review': 'warning',
  service: 'info',
};

const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

export default function CarModal({ open, stockNumber, onClose }) {
  const myZ = useAutoZ(open, stockNumber);
  const theme = useTheme();
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '95vh', margin: 1.5, radius: 2 });
  const { isAllowed, isAdmin, isManager, isPorter } = usePermissions();

  // The tab list this user actually gets. Everything downstream (the URL ?tab= lookup,
  // the click handler, the active-tab definition, the <Tab> list) indexes into this
  // same array, so it must be the single source of tab order.
  const canSeeFinancials = isAllowed('car-financials') || isAdmin();
  const visibleTabs = React.useMemo(
    () => VISIBLE_TABS.filter(t => canSeeFinancials || !FINANCIAL_TABS.includes(t)),
    [canSeeFinancials],
  );

  const [car, setCar] = React.useState({});
  const [activeTab, setActiveTab] = React.useState(0);
  const [duplicating, setDuplicating] = React.useState(false);
  const [ntoExpenseModalOpen, setNtoExpenseModalOpen] = React.useState(false);
  // The refresh button bumps this to reload the modal's data without reloading the page.
  const [refreshKey, setRefreshKey] = React.useState(0);
  const lastStockNumberRef = React.useRef(null);

  // Single effect: reset + load data, with cancellation to kill stale async callbacks
  React.useEffect(() => {
    // Clear state and reset the tab only when stockNumber changes (including when it
    // becomes null on close). A refresh keeps the current tab and reloads in place.
    if (lastStockNumberRef.current !== stockNumber) {
      lastStockNumberRef.current = stockNumber;
      setCar({});
      if (stockNumber) {
        // Read initial tab from URL before any async work
        const tabParam = new URL(window.location.href).searchParams.get('tab');
        const idx = visibleTabs.indexOf(tabParam);
        setActiveTab(idx >= 0 ? idx : 0);
      }
    }

    if (!stockNumber) return;

    let cancelled = false;

    const db = firebase.firestore();

    // The car document is the source of truth for all car-doc fields (specs,
    // bullets, writeup, notes, chat-applied edits, …). A realtime listener keeps
    // the shared `car` state live across tab changes regardless of which surface
    // wrote it (optimistic UI, the chat bot, or a background job). Heavy joined
    // data (invoices, deposits, orders, deal, customers…) is still loaded once;
    // it isn't what reverts between tabs.
    function loadSubCollections(data) {
        db.doc('inspections/' + stockNumber).get().then(snap => {
          if (cancelled) return;
          data.inspection = snap.data() || {};
          data.inspection_loaded = true;
          setCar(prev => ({ ...prev, inspection: data.inspection, inspection_loaded: true }));
        });

        db.collection('invoices').where('stock', '==', stockNumber).get().then(invoiceSnap => {
          if (cancelled) return;
          const [shipping, fail] = invoiceSnap.docs.map(getDocData).partition(x => x.type === 'shipping');
          const [shipping_in] = invoiceSnap.docs.map(getDocData).partition(x => x.type === 'shipping_in');
          const [finance, regular] = fail.partition(x => x.type === 'finance');
          data.invoices = regular.filter(x => !['shipping_in'].includes(x.type));
          data.shipping_invoices = shipping;
          data.shipping_in_invoices = shipping_in;
          data.finance_invoices = finance;
          data.invoices_loaded = true;
          setCar(prev => ({ ...prev, invoices: data.invoices, shipping_invoices: data.shipping_invoices, shipping_in_invoices: data.shipping_in_invoices, finance_invoices: data.finance_invoices, invoices_loaded: true }));
        });

        db.collection('deposits').where('stock', '==', stockNumber).get().then(transSnap => {
          if (cancelled) return;
          const [shipping, fail] = transSnap.docs.map(getDocData).partition(x => x.type === 'shipping');
          const [shipping_in] = transSnap.docs.map(getDocData).partition(x => x.type === 'shipping_in');
          const [finance, regular] = fail.partition(x => x.type === 'finance');
          data.deposits = regular.filter(x => !['shipping_in'].includes(x.type));
          data.shipping_deposits = shipping;
          data.shipping_in_deposits = shipping_in;
          data.finance_deposits = finance;
          data.deposits_loaded = true;
          setCar(prev => ({ ...prev, deposits: data.deposits, shipping_deposits: data.shipping_deposits, shipping_in_deposits: data.shipping_in_deposits, finance_deposits: data.finance_deposits, deposits_loaded: true }));
        });

        db.collection('purchases').where('stock', '==', stockNumber).get().then(expSnap => {
          if (cancelled) return;
          const [pass, fail] = expSnap.docs.map(getDocData).partition(x => x.type === 'shipping');
          const [shipping_in] = expSnap.docs.map(getDocData).partition(x => x.type === 'shipping_in');
          data.expenses = fail.filter(x => !['shipping_in'].includes(x.type));
          data.shipping_expenses = pass;
          data.shipping_in_expenses = shipping_in;
          data.nto_expense = data.expenses.find(x => (x.id || '').includes('NTO') || (x.memo || '').includes('NTO')) || null;
          data.expenses_loaded = true;
          setCar(prev => ({ ...prev, expenses: data.expenses, shipping_expenses: data.shipping_expenses, shipping_in_expenses: data.shipping_in_expenses, nto_expense: data.nto_expense, expenses_loaded: true }));
        });

        db.collection('repairs').where('stock', '==', stockNumber).get().then(repairSnap => {
          if (cancelled) return;
          data.repairs = repairSnap.docs.map(getDocData);
          data.repairs_loaded = true;
          setCar(prev => ({ ...prev, repairs: data.repairs, repairs_loaded: true }));
        });

        db.collection('leads').where('stock', '==', stockNumber).get().then(leadSnap => {
          if (cancelled) return;
          data.leads = leadSnap.docs.map(getDocData).map(lead => ({ ...lead, rowAction: () => StateManager.openLeadModal(lead.id) }));
          data.leads_loaded = true;
          setCar(prev => ({ ...prev, leads: data.leads, leads_loaded: true }));
        });

        db.collection('files').where('stock', '==', stockNumber).get().then(fileSnap => {
          if (cancelled) return;
          data.files = fileSnap.docs.map(getDocData);
          data.files_loaded = true;
          setCar(prev => ({ ...prev, files: data.files, files_loaded: true }));
        });

        db.collection('orders').where('car', '==', stockNumber).get().then(async ordersSnap => {
          if (cancelled) return;
          data.orders = ordersSnap.docs.map(getDocData);

          const svcPromises = data.orders.map(o => db.collection('services').where('order', '==', o.id).get());
          const svcSnaps = await Promise.all(svcPromises);
          if (cancelled) return;
          const services = svcSnaps.flatMap(s => s.docs.map(getDocData));

          const partPromises = data.orders.map(o => db.collection('parts').where('order', '==', o.id).get());
          const partSnaps = await Promise.all(partPromises);
          if (cancelled) return;
          const parts = partSnaps.flatMap(s => s.docs.map(getDocData));

          const subPromises = data.orders.map(o => db.collection('subcontracts').where('order', '==', o.id).get());
          const subSnaps = await Promise.all(subPromises);
          if (cancelled) return;
          const subs = subSnaps.flatMap(s => s.docs.map(getDocData));

          data.orders.forEach((order, i) => {
            data.orders[i].cost = (
              services.filter(x => x.order === order.id).reduce((a, c) => a + (c.cost || 0), 0) +
              parts.filter(x => x.order === order.id).reduce((a, c) => a + (c.cost || 0), 0) +
              subs.filter(x => x.order === order.id).reduce((a, c) => a + (c.cost || 0), 0)
            );
            data.orders[i].rowAction = (event) => {
              if (event.ctrlKey || event.metaKey) {
                window.open(`/service-dashboard?so=${order.id}`, '_blank');
              } else {
                StateManager.openServiceOrderModal(order.id);
              }
            };
            data.orders[i].rowLink = undefined;
          });
          data.orders_loaded = true;
          setCar(prev => ({ ...prev, orders: data.orders, orders_loaded: true }));
        });

        db.doc('titles/' + stockNumber).get().then(titleSnap => {
          if (cancelled) return;
          data.title = { ...titleSnap.data(), id: titleSnap.id };
          data.title_loaded = true;
          setCar(prev => ({ ...prev, title: data.title, title_loaded: true }));
        });

        db.doc('deals/' + stockNumber).get()
          .then(dealSnap => {
            if (cancelled) return;
            data.deal = dealSnap.data() || {};
            data.deal_loaded = true;
            setCar(prev => ({ ...prev, deal: data.deal, deal_loaded: true }));
          })
          .then(() => {
            if (cancelled) return;
            const customers = [
              { type: 'consignor', id: data.consignor },
              { type: 'buyer', id: data.deal.buyer },
              { type: 'cobuyer', id: data.deal.cobuyer },
            ];
            customers.forEach(({ type, id }) => {
              if (!id) {
                data[type] = {};
                data[`${type}_loaded`] = true;
                if (!cancelled) setCar(prev => ({ ...prev, [type]: {}, [`${type}_loaded`]: true }));
                return;
              }
              db.doc('customers/' + id).get().then(custDoc => {
                if (cancelled) return;
                const custData = { ...custDoc.data(), id };
                custData.display_name = `${custData.first_name || ''}${custData.last_name ? ' ' : ''}${custData.last_name || ''}`;
                data[type] = custData;
                data[`${type}_loaded`] = true;
                setCar(prev => ({ ...prev, [type]: custData, [`${type}_loaded`]: true }));
              });
            });
          });
    }

    // `initialized` gates the one-time joined-data load to the first snapshot
    // that sees the doc; later snapshots only merge fresh car-doc fields into the
    // shared state (preserving the joined sub-collections + their loaded flags).
    // `creating` guards against firing the create path more than once.
    let initialized = false;
    let creating = false;

    const unsubscribe = db.doc('cars/' + stockNumber).onSnapshot(async doc => {
      if (cancelled) return;

      if (doc.exists) {
        const docData = doc.data();
        if (!initialized) {
          initialized = true;
          const data = { ...docData, car_loaded: true, updater: setCar };
          setCar({ ...data });
          loadSubCollections(data);
        } else {
          // Live update — merge fresh car-doc fields, but DON'T let the
          // denormalized scalar IDs the car doc carries (buyer/cobuyer/consignor)
          // clobber the joined customer OBJECTS resolved during init. Those object
          // fields are only (re)built by loadSubCollections on the first snapshot,
          // so spreading the raw doc here would turn car.buyer back into an ID
          // string (dropping .email/.display_name) on every later write — e.g.
          // when createLabel writes tracking fields from the DMV tab.
          const { buyer, cobuyer, consignor, ...carFields } = docData;
          setCar(prev => ({ ...prev, ...carFields, car_loaded: true, updater: setCar }));
        }
      } else if (!initialized && !creating) {
        creating = true;
        setCar({ stock: stockNumber, updater: setCar });
        firebase.firestore().doc('cars/' + stockNumber).set({ stock: stockNumber }, { merge: true });
        // The set() above re-fires this listener with the created doc, which then
        // runs the full init path.
      }
    });

    StateManager.updateCar = (update) => setCar(prev => ({ ...prev, ...update }));

    return () => { cancelled = true; unsubscribe(); };
  }, [stockNumber, refreshKey]);

  React.useEffect(() => {
    if (!open || !stockNumber) return;
    const carInfo = `${car.year || ''} ${car.make || ''} ${car.model || ''}`.replace(/\s+/g, ' ').trim();
    document.title = carInfo ? `${stockNumber} - ${carInfo}` : `${stockNumber}`;
    return () => { document.title = StateManager.title || document.title; };
  }, [open, stockNumber, car.year, car.make, car.model]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        e.stopPropagation();
        handlePrintSummary();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, car]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    const tabKey = visibleTabs[newValue];
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabKey);
    history.replace(url.pathname + '?' + url.searchParams.toString());
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  // ── Computed values ──
  const titleForPrint = `${car.year || ''}${car.year ? ' ' : ''}${car.make || ''}${car.make ? ' ' : ''}${car.model || ''}${car.model ? ' ' : ''}${car.trim === 'Base' ? '' : car.trim || ''}`.trim();
  const slug = car.slug || (car.stock ? '/' + `${car.stock}-${titleForPrint}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '') : '');

  // A sold car is read-only for everyone but admins.
  const soldLocked = car.status === 'sold' && !isAdmin();

  const shouldEditStatus = car.car_loaded && !soldLocked && (
    isManager()
    || isPorter()
    || (isAdmin() && !['sold', 'terminated'].includes(car.status))
  );

  const publish_status = car.status
    ? (constants.statuses.slice(-4).includes(car.status) ? 'Removed' : constants.statuses.slice(0, 2).includes(car.status) ? 'Not Live' : 'Published')
    : null;

  // ── Action handlers (from CarSummary) ──
  const handleCopyLink = async () => {
    const carUrl = `https://skywayclassics.com/vehicles${slug}`;
    try {
      await navigator.clipboard.writeText(carUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = carUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    StateManager.setAlertAndOpen('Car link copied to clipboard!', 'success');
  };

  const handleCopyInfo = async () => {
    const infoText = `${car.id || car.stock || ''} ${car.year || ''} ${car.make || ''} ${car.model || ''} VIN: ${car.vin || ''}`.trim();
    try {
      await navigator.clipboard.writeText(infoText);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = infoText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    StateManager.setAlertAndOpen('Car info copied to clipboard!', 'success');
  };

  const handlePrintSticker = () => {
    const price = car.price
      ? car.price.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : 'Coming Soon';
    let writeup = car.writeup || 'Writeup Coming Soon';
    const engine = car['engine-type'] || 'Coming Soon';
    const transmission = car.transmission || 'Coming Soon';
    const maxLen = 3750;
    if (writeup.length > maxLen) writeup = writeup.substring(0, maxLen) + '...';
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write(`<html><head><title>Print</title><style>body{font-family:'Arial',sans-serif;margin:20px;}.print-container{text-align:center;}.title,.engine,.transmission,.stock{font-weight:bold;font-size:24px;margin-bottom:10px;}.price{font-size:20px;margin-bottom:20px;}.writeup{text-align:justify;margin-top:20px;}.label{font-weight:bold;}</style></head><body><div class="print-container"><div class="title">${titleForPrint}</div><div class="price">${price}</div><div class="stock"><span class="label">Stock Number:</span> ${car.stock}</div><div class="engine"><span class="label">Engine:</span> ${engine}</div><div class="transmission"><span class="label">Transmission:</span> ${transmission}</div><div class="writeup">${writeup}</div></div></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePrintSummary = () => {
    const summaryEl = document.querySelector('.section-to-print.sales-summary-print');
    if (!summaryEl) return;
    const printWindow = window.open('', '', 'height=900,width=800');
    printWindow.document.write(`<html><head><title>Print Summary</title><style>
      body{font-family:'Helvetica Neue',Arial,sans-serif;margin:20px;color:#2c3e50;}
      .ssp-header{display:flex;align-items:flex-start;gap:16px;margin-bottom:14px;border-bottom:2px solid #023e84;padding-bottom:12px;}
      .ssp-thumbnail{height:100px;width:auto;max-width:150px;object-fit:cover;border-radius:4px;flex-shrink:0;}
      .ssp-vehicle-block{flex:1;}
      .ssp-vehicle-title{font-size:15pt;font-weight:700;color:#023e84;margin:0 0 4px 0;}
      .ssp-vehicle-meta{font-size:8pt;color:#555;margin:2px 0;}
      .ssp-two-col{display:flex;gap:16px;margin-bottom:14px;}
      .ssp-two-col .ssp-ledger{flex:1;}
      .ssp-ledger{width:100%;border-collapse:collapse;border:1px solid #b0b8c4;font-size:8.5pt;page-break-inside:avoid;}
      .ssp-ledger thead th{background-color:#023e84;color:white;font-size:8.5pt;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:5px 10px;text-align:left;}
      .ssp-ledger tbody td{padding:5px 10px;border-bottom:1px solid #e8ecf0;font-size:8.5pt;}
      .ssp-ledger tbody td:last-child{text-align:right;font-variant-numeric:tabular-nums;}
      .ssp-debit{color:#666;}
      .ssp-ledger-total td{background-color:#f0f3f7;border-top:2px solid #7a8fa8;padding:6px 10px;font-weight:700;font-size:9pt;}
      .ssp-ledger-total td:last-child{text-align:right;font-variant-numeric:tabular-nums;}
      .ssp-negative{color:#c0392b;}
      .ssp-positive{color:#1a7a3c;}
      .ssp-paid{color:#1a7a3c;font-weight:700;}
      .ssp-owed{color:#023e84;}
      .ssp-section{margin-bottom:12px;page-break-inside:avoid;}
      .ssp-section-title{font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#023e84;border-bottom:1.5px solid #023e84;padding-bottom:3px;margin:0 0 5px 0;}
      .ssp-empty{font-size:8pt;color:#999;font-style:italic;margin:4px 0;}
      .ssp-table{width:100%;border-collapse:collapse;font-size:8pt;table-layout:auto;}
      .ssp-table th{text-align:left;font-weight:600;padding:3px 5px;border-bottom:1px solid #bbb;color:#444;font-size:7.5pt;}
      .ssp-table td{padding:3px 5px;border-bottom:1px dotted #e0e0e0;font-size:8pt;vertical-align:middle;}
      .ssp-table td:last-child,.ssp-table th:last-child{text-align:right;font-variant-numeric:tabular-nums;}
      .ssp-total-row td{font-weight:700;border-top:1.5px solid #aaa;border-bottom:none;padding-top:4px;font-size:8.5pt;}
      .ssp-expense-unpaid{opacity:0.7;}
      @page{margin:1cm;}
    </style></head><body onload="setTimeout(function(){window.print();window.close();},300)">${summaryEl.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
  };

  const handlePrintQR = () => {
    const printContent = document.getElementById('car-modal-qrcode');
    if (!printContent) return;
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write(`<html><head><title>Print</title><style>body{font-family:'Arial',sans-serif;margin:20px;}.print-container{text-align:center;}</style></head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleDuplicateCar = async () => {
    setDuplicating(true);
    try {
      const counters = await firebase.firestore().doc('admin/counters').get();
      const new_stock_num = counters.data().lastStock + 1;
      const newStock = 'SN' + new_stock_num.toString();
      const fieldsToCopy = [
        'year', 'make', 'model', 'trim', 'color', 'vin', 'miles', 'price',
        'body-style', 'door-count', 'engine-type', 'cylinders',
        'interior-color', 'interior-material', 'transmission', 'drivetrain',
        'location', 'is_actual', 'is_excess', 'is_exempt', 'odometer_5', 'odometer_6',
        'writeup', 'bullets',
        'youtube_link', 'walkaround_video', 'engine_video', 'convertible_video', 'driving_video', 'undercarriage_video',
        'thumbnail', 'vin_image', 'odometer_image',
        'stamping_images', 'ext_images', 'interior_images', 'engine_images', 'under_images',
      ];
      const today = moment().format('YYYY/MM/DD');
      const newCarData = { stock: newStock, date: today, inDA: false };
      fieldsToCopy.forEach(key => { if (car[key] !== undefined) newCarData[key] = car[key]; });
      if (newCarData.vin) newCarData.vin = newCarData.vin.trim().toUpperCase();
      newCarData.status = constants.statuses[0];
      newCarData.sub_status = constants.sub_statuses[0];
      newCarData.status_time = today;
      newCarData.sub_status_time = today;
      await firebase.firestore().doc('cars/' + newStock).set(newCarData);
      await firebase.firestore().doc('admin/counters').update({ lastStock: new_stock_num });
      StateManager.setAlertAndOpen(`Car duplicated as ${newStock}`, 'success');
      StateManager.openCarModal(newStock);
    } catch (err) {
      console.error('Error duplicating car:', err?.message || err);
      StateManager.setAlertAndOpen(`Failed to duplicate car: ${err?.message || 'unknown error'}`, 'error');
    } finally {
      setDuplicating(false);
    }
  };

  const handleStatusChange = async (key, value) => {
    const update = { [key]: value, [key + '_time']: moment().format('YYYY/MM/DD') };
    if (key === 'status' && ['active', 'success'].includes(value) && !car.activated_date) {
      update.activated_date = moment().format('YYYY/MM/DD');
    }
    await firebase.firestore().doc('cars/' + stockNumber).set(update, { merge: true });
    setCar(prev => ({ ...prev, ...update }));
  };

  const allTabDefs = {
    summary: { component: <CarSummary car={car} updater={setCar} />, condition: car.car_loaded },
    chat: { component: <Chat car={car} />, condition: car.car_loaded },
    checkin: { component: <Checkin car={car} updater={setCar} />, condition: car.inspection_loaded },
    inspection: { component: <Inspection car={car} updater={setCar} />, condition: car.inspection_loaded && car.orders_loaded },
    service: { component: <Service orders={car.orders} stockNumber={stockNumber} thumbnail={car.thumbnail} car={car} />, condition: car.car_loaded && car.orders_loaded },
    marketing: { component: <Marketing car={car} />, condition: car.car_loaded },
    facebook: { component: <Facebook car={car} />, condition: car.car_loaded },
    writeup: { component: <Writeup car={car} />, condition: car.car_loaded },
    'sales-review': { component: <SalesReview car={car} updater={setCar} />, condition: car.car_loaded },
    consignment: { component: <Consignor car={car} />, condition: car.consignor_loaded },
    leads: { component: <Leads {...car} />, condition: car.leads_loaded },
    sales: { component: <Sales car={car} />, condition: car.deal_loaded && car.buyer_loaded && car.cobuyer_loaded && car.deposits_loaded && car.expenses_loaded },
    finance: { component: <Finance car={car} />, condition: car.deal_loaded && car.buyer_loaded && car.cobuyer_loaded },
    'shipping-in': { component: <ShippingIn car={car} />, condition: car.deal_loaded && car.buyer_loaded && car.cobuyer_loaded && car.invoices_loaded },
    'shipping-out': { component: <Shipping car={car} />, condition: car.deal_loaded && car.buyer_loaded && car.cobuyer_loaded && car.invoices_loaded },
    // Only `car_loaded` on purpose: an unsold car has no buyer, and the
    // estimator still has to work there against a custom address.
    transport: { component: <Transport car={car} />, condition: car.car_loaded },
    DMV: { component: <DMV car={car} />, condition: car.deal_loaded },
    files: { component: <Files car={car} updater={setCar} readOnly={soldLocked} />, condition: car.files_loaded },
    notes: { component: <Notes car={car} />, condition: car.car_loaded },
  };

  const activeTabDef = allTabDefs[visibleTabs[activeTab]];
  // Chat messages the customer, not the car record. Files handles readOnly itself
  // so its Open button keeps working.
  const lockActiveTab = soldLocked && !['chat', 'files'].includes(visibleTabs[activeTab]);

  const deal = car.deal || {};
  const formatCurrency = amt => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt || 0);
  const activeInvoice = (car.invoices || []).find(x => x.id === deal.invoice) || {};
  const getTotalDeposits = () => (car.deposits || []).reduce((s, d) => s + (d.amount || 0), 0);
  const getTotalExpenses = () => (car.expenses || []).reduce((s, e) => s + (e.amount || 0), 0);

  const carTitle = [car.year, car.make, car.model].filter(Boolean).join(' ');
  const statusColor = STATUS_COLOR[car.status] || 'default';

  return (
    <ModalStackProvider value={myZ}>
      {/* Print-only accounting summary — only in DOM when modal is open to avoid showing on other pages */}
      {open && <div className="section-to-print sales-summary-print hidden-ro-print">
        <div className="ssp-header">
          {car.thumbnail && <img className="ssp-thumbnail" src={car.thumbnail} alt="" />}
          <div className="ssp-vehicle-block">
            <div className="ssp-vehicle-title">{carTitle}</div>
            <div className="ssp-vehicle-meta">Stock #{car.stock}{car.vin ? ` · VIN: ${car.vin}` : ''}</div>
            {car.color && <div className="ssp-vehicle-meta">Color: {car.color}{car.miles ? ` · Miles: ${Number(car.miles).toLocaleString()}` : ''}</div>}
            {(car.buyer || {}).display_name && <div className="ssp-vehicle-meta">Customer: {car.buyer.display_name}</div>}
            {deal.date && <div className="ssp-vehicle-meta">Sale Date: {deal.date}</div>}
            {deal.sales_rep && <div className="ssp-vehicle-meta">Sales Rep: {deal.sales_rep}</div>}
          </div>
        </div>
        <div className="ssp-two-col">
          <table className="ssp-ledger">
            <thead><tr><th colSpan={2}>Profit</th></tr></thead>
            <tbody>
              <tr><td>Invoice Total</td><td>{formatCurrency(activeInvoice.total)}</td></tr>
              <tr><td>Expenses</td><td className="ssp-debit">({formatCurrency(getTotalExpenses())})</td></tr>
            </tbody>
            <tfoot>
              <tr className="ssp-ledger-total">
                <td>Net Profit</td>
                <td className={(activeInvoice.total || 0) - getTotalExpenses() < 0 ? 'ssp-negative' : 'ssp-positive'}>
                  {formatCurrency((activeInvoice.total || 0) - getTotalExpenses())}
                </td>
              </tr>
            </tfoot>
          </table>
          <table className="ssp-ledger">
            <thead><tr><th colSpan={2}>Balance Due</th></tr></thead>
            <tbody>
              <tr><td>Invoice Total</td><td>{formatCurrency(activeInvoice.total)}</td></tr>
              <tr><td>Deposits Received</td><td className="ssp-debit">({formatCurrency(getTotalDeposits())})</td></tr>
            </tbody>
            <tfoot>
              {(() => {
                const owed = (activeInvoice.total || 0) - getTotalDeposits();
                return (
                  <tr className="ssp-ledger-total">
                    <td>Amount Owed</td>
                    <td className={owed <= 0 ? 'ssp-paid' : 'ssp-owed'}>{owed <= 0 ? 'PAID IN FULL' : formatCurrency(owed)}</td>
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        </div>
        {activeInvoice.total ? (
          <div className="ssp-section">
            <h3 className="ssp-section-title">Invoice Detail</h3>
            <table className="ssp-table">
              <tbody>
                {activeInvoice.cashPrice ? <tr><td>Cash Price</td><td>{formatCurrency(activeInvoice.cashPrice)}</td></tr> : null}
                {activeInvoice.docFee ? <tr><td>Doc Fee</td><td>{formatCurrency(activeInvoice.docFee)}</td></tr> : null}
                {activeInvoice.titleFee ? <tr><td>Title Fee</td><td>{formatCurrency(activeInvoice.titleFee)}</td></tr> : null}
                {activeInvoice.tagFee ? <tr><td>Tag Fee</td><td>{formatCurrency(activeInvoice.tagFee)}</td></tr> : null}
                {activeInvoice.conciergeFee ? <tr><td>Concierge Fee</td><td>{formatCurrency(activeInvoice.conciergeFee)}</td></tr> : null}
                {activeInvoice.taxes ? <tr><td>Taxes</td><td>{formatCurrency(activeInvoice.taxes)}</td></tr> : null}
                {activeInvoice.adjustments ? <tr><td>Adjustments</td><td>{formatCurrency(activeInvoice.adjustments)}</td></tr> : null}
              </tbody>
              <tfoot>
                <tr className="ssp-total-row"><td>Invoice Total</td><td>{formatCurrency(activeInvoice.total)}</td></tr>
              </tfoot>
            </table>
          </div>
        ) : null}
        <div className="ssp-section">
          <h3 className="ssp-section-title">Deposits</h3>
          {(car.deposits || []).length > 0 ? (
            <table className="ssp-table">
              <thead><tr><th>Date</th><th>Memo</th><th>Amount</th></tr></thead>
              <tbody>
                {(car.deposits || []).map((dep, i) => (
                  <tr key={i}><td>{dep.date || '—'}</td><td>{dep.memo || '—'}</td><td>{formatCurrency(dep.amount)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="ssp-total-row"><td colSpan={2}>Total Deposits</td><td>{formatCurrency(getTotalDeposits())}</td></tr>
              </tfoot>
            </table>
          ) : <p className="ssp-empty">No deposits recorded.</p>}
        </div>
        <div className="ssp-section">
          <h3 className="ssp-section-title">Expenses</h3>
          {(car.expenses || []).length > 0 ? (
            <table className="ssp-table">
              <thead><tr><th>Paid</th><th>Date</th><th>Memo</th><th>Amount</th></tr></thead>
              <tbody>
                {(car.expenses || []).map((exp, i) => (
                  <tr key={i} className={exp.isPayable ? 'ssp-expense-unpaid' : ''}>
                    <td>{!exp.isPayable ? '✓' : '☐'}</td>
                    <td>{exp.date || '—'}</td>
                    <td>{exp.memo || '—'}</td>
                    <td>{formatCurrency(exp.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="ssp-total-row"><td colSpan={3}>Total Expenses</td><td>{formatCurrency(getTotalExpenses())}</td></tr>
              </tfoot>
            </table>
          ) : <p className="ssp-empty">No expenses recorded.</p>}
        </div>
      </div>}

      {/* Main Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        fullScreen={fullScreen}
        sx={{ zIndex: myZ }}
        PaperProps={{
          sx: {
            ...paperSx,
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Top bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {car.stock ? `${car.stock}${carTitle ? '  ·  ' + carTitle : ''}` : 'Loading…'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton aria-label="refresh" onClick={() => setRefreshKey(k => k + 1)} size="small" sx={{ color: theme.palette.text.secondary }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Tab bar ── */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0, px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.8rem', textTransform: 'none', fontWeight: 500 },
              '& .Mui-selected': { fontWeight: 700 },
            }}
          >
            {visibleTabs.map(key => (
              <Tab key={key} label={formatTitle(key)} />
            ))}
          </Tabs>
        </Box>

        {/* ── Scrollable content (profile header scrolls with content) ── */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: theme.palette.grey[100] },
            '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[300], borderRadius: 3 },
          }}
        >
          {/* ── Profile header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 2.5 },
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              bgcolor: alpha(theme.palette.grey[50], 0.8),
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexWrap: 'wrap',
            }}
          >
            {/* Thumbnail — responsive size */}
            <Box
              sx={{
                display: 'flex',
                width: { xs: 96, sm: 180 },
                height: { xs: 72, sm: 135 },
                borderRadius: 1.5,
                overflow: 'hidden',
                flexShrink: 0,
                boxShadow: theme.shadows[2],
                bgcolor: theme.palette.grey[100],
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {car.thumbnail ? (
                <Box component={SizedImg} photoWidth={800} src={car.thumbnail} alt={carTitle} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <DirectionsCarIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: theme.palette.grey[400] }} />
              )}
            </Box>

            {/* Car info + icon buttons */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5, fontSize: '1rem' }} noWrap>
                {carTitle || '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {car.stock && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    #{car.stock}
                  </Typography>
                )}
                {car.vin && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
                    VIN: {car.vin}
                  </Typography>
                )}
                {car.miles && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
                    {Number(car.miles).toLocaleString()} mi
                  </Typography>
                )}
              </Box>
              {/* Icon buttons — sit beneath stock / VIN */}
              {car.car_loaded && (
                <Box sx={{ display: 'flex', gap: 0.25, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Tooltip title="Copy car link">
                    <IconButton size="small" onClick={handleCopyLink} sx={{ color: theme.palette.text.secondary }}>
                      <InsertLinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy car info">
                    <IconButton size="small" onClick={handleCopyInfo} sx={{ color: theme.palette.text.secondary }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print window sticker">
                    <IconButton size="small" onClick={handlePrintSticker} sx={{ color: theme.palette.text.secondary }}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print sales summary">
                    <IconButton size="small" onClick={handlePrintSummary} sx={{ color: theme.palette.text.secondary }}>
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print QR code">
                    <IconButton size="small" onClick={handlePrintQR} sx={{ color: theme.palette.text.secondary }}>
                      <QrCode2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isAdmin() && (
                    <Tooltip title={duplicating ? 'Duplicating…' : 'Duplicate car'}>
                      <span>
                        <IconButton size="small" onClick={handleDuplicateCar} disabled={duplicating} sx={{ color: '#7b1fa2' }}>
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
              {/* Publish + video status */}
              {car.car_loaded && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {publish_status === 'Published' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <CloudDoneIcon sx={{ fontSize: 13, color: theme.palette.success.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>Live</Typography>
                    </Box>
                  )}
                  {publish_status === 'Not Live' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <PauseCircleFilledIcon sx={{ fontSize: 13, color: theme.palette.text.disabled }} />
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 600 }}>Not Live</Typography>
                    </Box>
                  )}
                  {publish_status === 'Removed' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <HighlightOffIcon sx={{ fontSize: 13, color: theme.palette.error.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>Removed</Typography>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                    Video: {car.youtube_link ? 'Live' : 'Pending'}
                  </Typography>
                </Box>
              )}
              {car.expenses_loaded && isAllowed('edit-updated-nto') && (car.nto || car.updated_nto) && (() => {
                const ntoAmount = car.updated_nto || car.nto;
                const isPaid = car.nto_expense && !car.nto_expense.isPayable;
                const isClickable = isAdmin() && !!car.nto_expense;
                return (
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5,
                      cursor: isClickable ? 'pointer' : 'default',
                      ...(isClickable && { '&:hover': { opacity: 0.75 } }),
                    }}
                    onClick={() => isClickable && setNtoExpenseModalOpen(true)}
                  >
                    {isPaid ? (
                      <CheckCircleIcon sx={{ fontSize: 13, color: theme.palette.success.main }} />
                    ) : (
                      <ScheduleIcon sx={{ fontSize: 13, color: theme.palette.warning.dark }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: isPaid ? theme.palette.success.main : theme.palette.warning.dark,
                        fontWeight: 500,
                      }}
                    >
                      NTO: ${Number(ntoAmount).toLocaleString()} · {isPaid ? 'Paid' : 'Unpaid'}
                    </Typography>
                  </Box>
                );
              })()}
              {car.expenses_loaded && car.nto_expense?.nto_scheduled_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    NTO payment scheduled: {moment(car.nto_expense.nto_scheduled_date).format('MMM D, YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Status controls */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                flexWrap: 'wrap',
                gap: 1,
                alignItems: { xs: 'center', sm: 'flex-start' },
                width: { xs: '100%', sm: 'auto' },
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {shouldEditStatus ? (
                  <>
                    <FormControl size="small">
                      <Select
                        value={car.status || ''}
                        onChange={(e) => handleStatusChange('status', e.target.value)}
                        sx={{ minWidth: 100, textTransform: 'capitalize', fontSize: '0.85rem' }}
                      >
                        {constants.statuses
                          .filter(s => s !== 'active' || isManager())
                          .map(s => (
                            <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <Select
                        value={car.sub_status || ''}
                        onChange={(e) => handleStatusChange('sub_status', e.target.value)}
                        sx={{ minWidth: 100, textTransform: 'capitalize', fontSize: '0.85rem' }}
                      >
                        {constants.sub_statuses.map(s => (
                          <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <>
                    {car.status && (
                      <Chip label={car.status} color={statusColor} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                    )}
                    {car.sub_status && (
                      <Chip label={car.sub_status} variant="outlined" size="small" sx={{ textTransform: 'capitalize' }} />
                    )}
                  </>
                )}
                {car.deal_loaded && (
                  <ShippingStatusChip
                    stock={stockNumber}
                    deal={deal}
                    allowUnship={!soldLocked && (StateManager.isAdmin() || StateManager.isBackoffice())}
                    onUnshipped={() => setCar(prev => ({ ...prev, deal: { ...prev.deal, shipping_complete: false, shipping_status: '' } }))}
                  />
                )}
              </Box>
              {car.status_time && (
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, pl: 0.25 }}>
                  Updated {car.status_time}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Tab content */}
          <Box sx={{ p: 2 }}>
            {soldLocked && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This car is sold. Only admins can edit it.
              </Alert>
            )}
            <SoldLockContext.Provider value={lockActiveTab}>
              <SoldLock locked={lockActiveTab}>
                {activeTabDef
                  ? (activeTabDef.condition ? activeTabDef.component : <Loading />)
                  : <Loading />
                }
              </SoldLock>
            </SoldLockContext.Provider>
          </Box>
        </Box>

        {/* Hidden QR code element used for printing */}
        <div id="car-modal-qrcode" className="print-container" style={{ display: 'none' }}>
          {stockNumber && <QRCode value={`https://tools.skywayclassics.com/handle-scan/${stockNumber}`} />}
        </div>
      </Dialog>

      {car.nto_expense && (
        <ExpenseModal
          open={ntoExpenseModalOpen}
          onClose={() => setNtoExpenseModalOpen(false)}
          expense={car.nto_expense}
          onSaved={(updated) => setCar(prev => ({ ...prev, nto_expense: updated }))}
        />
      )}
    </ModalStackProvider>
  );
}
